import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getRestrictionComparer } from "../../../components/shared/services/DeliveryRestrictionService";

// ============================================================
// Remove Delivery Restriction Request — self-contained slice
//
// Holds every piece of Redux state/action/reducer logic used exclusively by the
// Remove Delivery Restriction Request feature (lender creation wizard + PS review
// section). Anything shared with other request types (initLenderRequest,
// resetLenderRequest, selectRequestSubTypesByName, saveAttachments, projectFieldsMap,
// etc.) stays in lenderRequestActions.js / lenderRequestReducer.js.
//
// See FeatureSpecs/RemoveDeliveryRestrictionRequest/important_decisions.md DEC-023.
// ============================================================

function initialState() {
  return {
    currentStep: 1,
    selectedRestrictions: [],
    uploadedDocuments: [],
    requestNotes: "",
    availableRestrictions: null,
    currentRestrictions: null,
    isSubmitting: false,
    submissionError: null,
    createdRequestId: null,
    requestCreatedButFilesFailed: false,
    reviewDecisions: {},
    isReviewing: false,
    reviewError: null
  };
}

const removeDeliveryRestrictionSlice = createSlice({
  name: "removeDeliveryRestriction",
  initialState: initialState(),
  reducers: {
    setRemoveDRStep(state, action) {
      state.currentStep = action.payload;
    },
    setAvailableRestrictions(state, action) {
      state.availableRestrictions = action.payload.availableRestrictions;
      state.currentRestrictions = action.payload.currentRestrictions;
    },
    toggleRestrictionSelection(state, action) {
      const exists = state.selectedRestrictions.find(
        r => r.restrcnId === action.payload.restrcnId
      );
      state.selectedRestrictions = exists
        ? state.selectedRestrictions.filter(r => r.restrcnId !== action.payload.restrcnId)
        : [...state.selectedRestrictions, action.payload];
    },
    addRemoveDRDocument(state, action) {
      state.uploadedDocuments.push(action.payload);
    },
    removeRemoveDRDocument(state, action) {
      state.uploadedDocuments = state.uploadedDocuments.filter(
        doc => doc.id !== action.payload
      );
    },
    setRemoveDRNotes(state, action) {
      state.requestNotes = action.payload;
    },
    setRemoveDRPartialFailure(state, action) {
      state.requestCreatedButFilesFailed = action.payload;
    },
    submitRequestStarted(state) {
      state.isSubmitting = true;
      state.submissionError = null;
    },
    submitRequestSucceeded(state, action) {
      state.isSubmitting = false;
      state.createdRequestId = action.payload;
    },
    submitRequestFailed(state, action) {
      state.isSubmitting = false;
      state.submissionError = action.payload;
    },
    resetRemoveDRWizard() {
      // Matches the previous REMOVE_DR_RESET_WIZARD behavior: availableRestrictions/
      // currentRestrictions reset to empty arrays rather than null, since a reset only
      // ever happens after restrictions have already been loaded once.
      return {
        ...initialState(),
        availableRestrictions: [],
        currentRestrictions: []
      };
    },
    submitReviewStarted(state) {
      state.isReviewing = true;
      state.reviewError = null;
    },
    submitReviewSucceeded(state) {
      state.isReviewing = false;
    },
    submitReviewFailed(state, action) {
      state.isReviewing = false;
      state.reviewError = action.payload;
    }
  }
});

export const {
  setRemoveDRStep,
  setAvailableRestrictions,
  toggleRestrictionSelection,
  addRemoveDRDocument,
  removeRemoveDRDocument,
  setRemoveDRNotes,
  setRemoveDRPartialFailure,
  submitRequestStarted,
  submitRequestSucceeded,
  submitRequestFailed,
  resetRemoveDRWizard,
  submitReviewStarted,
  submitReviewSucceeded,
  submitReviewFailed
} = removeDeliveryRestrictionSlice.actions;

export default removeDeliveryRestrictionSlice.reducer;

// ---- Selectors ----
export const selectAvailableRestrictions = (state) => state.removeDeliveryRestriction.availableRestrictions;
export const selectCurrentRestrictions = (state) => state.removeDeliveryRestriction.currentRestrictions;
export const selectRemoveDRStep = (state) => state.removeDeliveryRestriction.currentStep;
export const selectSelectedRestrictions = (state) => state.removeDeliveryRestriction.selectedRestrictions;
export const selectRemoveDRNotes = (state) => state.removeDeliveryRestriction.requestNotes;
export const selectCreatedRequestId = (state) => state.removeDeliveryRestriction.createdRequestId;
export const selectRequestCreatedButFilesFailed = (state) => state.removeDeliveryRestriction.requestCreatedButFilesFailed;

// ---- Thunks ----
export function fetchDeliveryRestrictions(projectId) {
  return async function (dispatch) {
    try {
      // Imported lazily (rather than at module scope) to avoid a circular import:
      // this slice is registered as a reducer in redux/reducers/index.js, and
      // userAction.js imports the store, which imports reducers/index.js.
      const { isInternal, isProjectReadOnlyRole } = await import("../../actions/userAction");

      const passData = JSON.parse(sessionStorage.getItem('passData'));
      const lenderId = sessionStorage.getItem('lenderId') || passData?.sellerServicerCoNo || '12346';

      // Step 1: Get project restrictions (same as Project page)
      const response = await axios.get(
        isInternal() || isProjectReadOnlyRole()
          ? global.REACT_APP_API_ENDPOINT + `/condoMgr/getPersRestrictionConditions/` + projectId
          : global.REACT_APP_API_ENDPOINT + `/condoMgr/getExternalPersRestrictionConditions/` + projectId + `/` + lenderId
      );

      if (!response.data) {
        throw new Error('Failed to fetch delivery restrictions');
      }

      if (!response.data.restrictionsConditionsList) {
        return [];
      }

      // Step 2: Filter to get project-level delivery restrictions (same logic as Project page)
      const restrictionsData = response.data.restrictionsConditionsList;
      const projectDR = restrictionsData?.filter(c =>
        (c.type === 'Restriction' || c.type === 'Manual Restriction')
        && !c.isDeleted
        && c.phaseId === 0
      ) || [];

      // If no restrictions found, try certification restrictions
      const deliveryRestrictions = projectDR.length > 0
        ? projectDR
        : restrictionsData?.filter(c => c.type === 'Certification Restriction' && c.phaseId === 0) || [];

      // Step 3: Fetch all pre-canned restrictions
      const cannedResponse = await axios.get(
        global.REACT_APP_API_ENDPOINT + `/delvyRestrictions/all-pre-canned`
      );

      const cannedRestrictions = cannedResponse.data?.restrictions || cannedResponse.data || [];

      // Step 4: Get the restriction names from project restrictions
      const projectRestrictionNames = new Set(deliveryRestrictions.map(r => r.decisionName).filter(Boolean));

      // Step 5: Filter canned restrictions to only those on the project.
      // Drop nulls or anything falsy.
      // Normalize property names for consistency (support both variants).
      // Sort by restrictionName for consistent display order.
      const normalizedRestrictions = cannedRestrictions
        .filter(Boolean)
        .map(canned => ({
          restrictionId: canned.restrictionId || canned.restrcnId,
          restrcnId: canned.restrictionId || canned.restrcnId,
          restrictionName: canned.restrictionName || canned.restrcnName,
          restrcnName: canned.restrictionName || canned.restrcnName,
          restrictionCode: canned.restrictionCode || canned.restrcnCode,
          restrcnCode: canned.restrictionCode || canned.restrcnCode,
          restrictionDescription: canned.restrictionDescription || canned.description,
          restrcnDescription: canned.restrictionDescription || canned.description,
          ...canned
        }))
        .sort(getRestrictionComparer('restrictionName',));

      const filteredRestrictions = normalizedRestrictions
        .filter(canned => {
          const cannedName = canned.restrictionName;
          const matches = projectRestrictionNames.has(cannedName);
          return matches && !!cannedName;
        });

      console.log('[REMOVE_DR] Fetched and filtered restrictions:', filteredRestrictions);

      const payload = {
        availableRestrictions: normalizedRestrictions,
        currentRestrictions: filteredRestrictions
      };

      dispatch(setAvailableRestrictions(payload));

      return payload;

    } catch (error) {
      console.error('[REMOVE_DR] Error fetching restrictions:', error);

      dispatch(setAvailableRestrictions({
        availableRestrictions: [],
        currentRestrictions: []
      }));
    }
  };
}

export function submitRemoveDRRequest(payload) {
  return async function (dispatch) {
    dispatch(submitRequestStarted());

    try {
      const response = await axios.post(
        global.REACT_APP_API_ENDPOINT + `/lender/request/status-change`,
        payload
      );

      if (response.status === 200 && +response.data > 0) {
        const requestId = +response.data;

        dispatch(submitRequestSucceeded(requestId));

        return requestId;
      } else {
        throw new Error('Failed to submit Remove DR request');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit request';

      dispatch(submitRequestFailed(errorMessage));

      throw error;
    }
  };
}

export function submitRemoveDRReview(requestId, projectId, reviewPayload) {
  return async function (dispatch) {
    dispatch(submitReviewStarted());

    try {
      const response = await axios.post(
        global.REACT_APP_API_ENDPOINT + `/lender/request/review-status-request/${requestId}/${projectId}`,
        reviewPayload
      );

      if (response.status === 200) {
        dispatch(submitReviewSucceeded());
        return response.data;
      } else {
        throw new Error('Failed to submit review');
      }
    } catch (error) {
      console.error('[REMOVE_DR] Error submitting review:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit review';

      dispatch(submitReviewFailed(errorMessage));

      throw error;
    }
  };
}
